import { useEffect, useMemo, useRef, useState } from "react";
import {
  MAX_ENRICH_ITEMS,
  applyReview,
  buildEnrichPrompt,
  importEnrichment,
  reviewRows,
  thumbPath,
  validId,
  type EnrichProposal,
  getDisplayCurrency,
  type Item,
  type ReviewChoice,
  type ReviewRow,
} from "@iltocat/shared";
import { ApiError, api, plural } from "../lib/api.js";
import { copyPhotoToClipboard } from "../lib/clipboardImage.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import type { Vocabulary } from "../lib/useVocabulary.js";
import { AskModel } from "../components/AskModel.js";
import { EnrichPicker } from "../components/EnrichPicker.js";
import { EnrichReview } from "../components/EnrichReview.js";
import { Icon } from "../components/Icon.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";
import { useToast } from "../components/Toast.js";

/**
 * Enrichment, start to finish: choose items, copy a prompt out, paste the
 * model's JSON back, review it against what is there, and save what was
 * accepted.
 *
 * **There is no model of ours in this app.** The prompt goes out through the
 * clipboard and the reply comes back the same way, which is what keeps the
 * owner's data and the owner's choice of model between the owner and that
 * model. With their own Anthropic or Google key, `AskModel` makes the same round trip
 * from this tab instead: the same prompt out, the reply dropped into the paste
 * box, and the same review. The three modules doing the work --
 * `buildEnrichPrompt`, `importEnrichment`, `reviewRows`/`applyReview` -- are
 * pure and separate so that the direct call replaced only the clipboard.
 *
 * The chosen ids live in the address (`?ids=a,b`), the same way search keeps its
 * state there, so a reload or the back button returns to the same prompt rather
 * than to an empty picker. The reply and the review do not: a pasted reply is
 * against one prompt at one moment and there is nothing to restore it to.
 */

/** What saving did to one item. */
type Outcome = { state: "saved"; count: number } | { state: "failed"; reason: string };

interface Reviewed {
  item: Item;
  rows: ReviewRow[];
}

const idsFromAddress = (): string[] =>
  (new URLSearchParams(window.location.search).get("ids") ?? "")
    .split(",")
    .filter(validId)
    .slice(0, MAX_ENRICH_ITEMS);

export function Enrich({
  taxonomy,
  vocabulary,
  onOpen,
  onSaved,
  onSettings,
}: {
  taxonomy: Taxonomy;
  vocabulary: Vocabulary;
  onOpen: (id: string) => void;
  /** Where model keys are added, for the Ask buttons' link. */
  onSettings: () => void;
  /** A single item's changes all saved: return to it. */
  onSaved: (id: string) => void;
}) {
  const [ids, setIds] = useState<string[]>(idsFromAddress);
  const [items, setItems] = useState<Item[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [includePrivate, setIncludePrivate] = useState(false);
  const [copyProblem, setCopyProblem] = useState<string | null>(null);
  const toast = useToast();
  const promptBox = useRef<HTMLTextAreaElement>(null);

  const [reply, setReply] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [reviewed, setReviewed] = useState<Reviewed[] | null>(null);
  const [choices, setChoices] = useState<Record<string, Record<string, ReviewChoice>>>({});
  const [busy, setBusy] = useState(false);
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});

  // Keep the address in step with the choice, without a history entry per tick.
  // The entry's state is kept: it says where this screen was opened from.
  useEffect(() => {
    window.history.replaceState(
      window.history.state,
      "",
      ids.length ? `/enrich?ids=${ids.join(",")}` : "/enrich",
    );
  }, [ids]);

  useEffect(() => {
    if (ids.length === 0) {
      setItems(null);
      return;
    }
    let live = true;
    setLoadError(null);
    void Promise.allSettled(ids.map((id) => api.items.get(id))).then((results) => {
      if (!live) return;
      const found = results.flatMap((result) => (result.status === "fulfilled" ? [result.value.item] : []));
      if (found.length === 0) {
        setLoadError("None of those items could be loaded. They may have been deleted.");
        return;
      }
      if (found.length < ids.length) {
        setLoadError(`${plural(ids.length - found.length, "item")} could not be loaded and was left out.`);
      }
      setItems(found);
    });
    return () => {
      live = false;
    };
  }, [ids]);

  const categoryName = (item: Item): string | null =>
    taxonomy.categories.find((category) => category.id === item.categoryId)?.name ?? null;

  /** The copied prompt never has photos attached; asking a model directly may. */
  const promptFor = (photosAttached: boolean): string =>
    items
      ? buildEnrichPrompt(
          items.map((item) => ({ item, categoryName: categoryName(item) })),
          {
            includePrivate,
            categoryNames: taxonomy.categories.map((category) => category.name),
            currency: getDisplayCurrency(),
            photosAttached,
          },
        )
      : "";

  // `promptFor` closes over the categories, which are listed here.
  const prompt = useMemo(() => promptFor(false), [items, includePrivate, taxonomy.categories]);

  const nameOf = (item: Item): string => item.title || "Untitled item";

  async function copy() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopyProblem(null);
      toast("Copied. Paste it into your chat.");
    } catch {
      // Clipboard access can be refused (an insecure origin, a permissions
      // policy). Selecting the text leaves a person one keystroke from done.
      promptBox.current?.focus();
      promptBox.current?.select();
      setCopyProblem("Your browser would not copy for us. The prompt is selected: press Ctrl+C or ⌘C.");
    }
  }

  async function copyPhoto(id: string) {
    try {
      await copyPhotoToClipboard(id);
      toast("Photo copied. Paste it into your chat.");
    } catch {
      setCopyProblem("Your browser would not copy that photo. Save it from the item and attach it instead.");
    }
  }

  /** `text` is passed when a model has just answered, before `reply` has caught up. */
  function review(text: string = reply) {
    if (!items) return;
    setProblem(null);
    const result = importEnrichment(
      text,
      items.map((item) => ({ id: item.id, name: nameOf(item) })),
    );
    if (!result.ok) {
      setProblem(result.reason);
      setReviewed(null);
      return;
    }

    const byItem = new Map<string, EnrichProposal>(result.proposals.map((p) => [p.itemId, p]));
    const built: Reviewed[] = [];
    const starting: Record<string, Record<string, ReviewChoice>> = {};
    for (const item of items) {
      const proposal = byItem.get(item.id);
      if (!proposal) continue;
      const rows = reviewRows(item, proposal, taxonomy.categories);
      built.push({ item, rows });
      starting[item.id] = Object.fromEntries(
        rows.map((row) => [row.key, { action: row.action, value: row.proposed }]),
      );
    }
    setWarnings(result.warnings);
    setChoices(starting);
    setOutcomes({});
    setReviewed(built);
  }

  /** How many rows are on Accept or Edit, across items not yet saved. */
  const pending = (reviewed ?? []).reduce(
    (total, { item, rows }) =>
      outcomes[item.id]?.state === "saved"
        ? total
        : total +
          rows.filter((row) => (choices[item.id]?.[row.key]?.action ?? row.action) !== "ignore").length,
    0,
  );

  async function save() {
    if (!reviewed) return;
    setBusy(true);
    const next: Record<string, Outcome> = { ...outcomes };
    // A category the reply named that does not exist is made once, however many
    // items want it.
    const made = new Map<string, string>();

    for (const { item, rows } of reviewed) {
      if (next[item.id]?.state === "saved") continue;
      const applied = applyReview(item, rows, choices[item.id] ?? {}, taxonomy.categories);
      if (!applied.ok) {
        next[item.id] = { state: "failed", reason: applied.reason };
        continue;
      }
      if (applied.accepted === 0) continue;

      try {
        if (applied.newCategory) {
          const key = applied.newCategory.toLowerCase();
          let id = made.get(key);
          if (!id) {
            id = (await taxonomy.addCategory(applied.newCategory))?.id;
            if (id) made.set(key, id);
          }
          if (id) applied.patch.categoryId = id;
        }
        await api.items.update(item.id, applied.patch);
        next[item.id] = { state: "saved", count: applied.accepted };
      } catch (failure) {
        next[item.id] = {
          state: "failed",
          reason: failure instanceof ApiError ? failure.message : "That did not save.",
        };
      }
    }

    setOutcomes(next);
    setBusy(false);
    // Counts, tags and categories all moved.
    void taxonomy.reload();
    void vocabulary.reloadTags();

    // One item, saved cleanly: back to it, which is where this usually started.
    // With several, or a failure, the review stays to say what happened.
    const only = reviewed.length === 1 ? reviewed[0]!.item : null;
    const outcome = only ? next[only.id] : undefined;
    if (only && outcome?.state === "saved") {
      toast(`Saved ${plural(outcome.count, "change")}.`);
      onSaved(only.id);
    }
  }

  /* ---------------------------------------------------------- the steps -- */

  const step = ids.length === 0 ? 0 : reviewed ? 3 : reply.trim() ? 2 : 1;

  if (ids.length === 0) {
    return (
      <>
        <PageHeader title="Fill in details" />
        <Steps at={step} />
        <EnrichPicker onChoose={setIds} />
      </>
    );
  }

  if (!items) {
    return (
      <>
        <PageHeader title="Fill in details" />
        <Steps at={step} />
        {loadError ? (
          <>
            <Notice tone="error">{loadError}</Notice>
            <button type="button" className="button button-quiet" onClick={() => setIds([])}>
              Choose again
            </button>
          </>
        ) : (
          <div className="panel skeleton-panel" />
        )}
      </>
    );
  }

  const anyPrivate = items.some(
    (item) => item.notes.trim() || item.fields.some((field) => field.visibility === "private"),
  );

  return (
    <>
      <PageHeader
        title="Fill in details"
        meta={<span>{items.length === 1 ? `“${nameOf(items[0]!)}”` : plural(items.length, "item")}</span>}
        actions={
          <button type="button" className="button button-ghost" onClick={() => setIds([])}>
            <Icon name="pencil" size={16} />
            Change items
          </button>
        }
      />
      <Steps at={step} />

      {loadError && <Notice tone="error">{loadError}</Notice>}

      <div className="enrich-grid">
        <Panel className="step-panel">
          <StepTitle n={2} title="Copy the prompt" />

          <label className="toggle">
            <input
              type="checkbox"
              checked={includePrivate}
              disabled={!anyPrivate}
              onChange={(event) => {
                setIncludePrivate(event.target.checked);
                setCopyProblem(null);
              }}
            />
            <span className="toggle-track" aria-hidden="true" />
            <span>
              Include private notes and fields
              <span className="toggle-hint">
                {anyPrivate ? "They go to whichever model you send this to." : "None of these items has any."}
              </span>
            </span>
          </label>

          <textarea
            ref={promptBox}
            className="input input-area mono"
            rows={8}
            readOnly
            aria-label="The prompt"
            value={prompt}
          />

          {copyProblem && <Notice tone="error">{copyProblem}</Notice>}

          {items.some((item) => item.photos.length > 0) && (
            <div className="enrich-photos">
              <span className="muted small">Photos: copy one, then paste it into the chat.</span>
              <ul className="enrich-photo-list">
                {items.flatMap((item) =>
                  item.photos.map((photo, index) => (
                    <li key={photo.id}>
                      <button
                        type="button"
                        className="enrich-photo"
                        aria-label={`Copy photo ${index + 1} of ${nameOf(item)}`}
                        onClick={() => void copyPhoto(photo.id)}
                      >
                        <img src={thumbPath(photo.id)} alt="" loading="lazy" />
                        <span className="enrich-photo-badge">
                          <Icon name="copy" size={14} />
                        </span>
                      </button>
                    </li>
                  )),
                )}
              </ul>
            </div>
          )}

          <div className="step-foot">
            <span className="muted small">
              {prompt.length.toLocaleString()} characters. Copy photos separately.
            </span>
            <button type="button" className="button" onClick={() => void copy()}>
              <Icon name="copy" size={18} />
              Copy prompt
            </button>
          </div>
        </Panel>

        <Panel className="step-panel">
          <StepTitle n={3} title="Paste the reply" />
          <AskModel
            items={items}
            promptFor={promptFor}
            onReply={(text) => {
              setReply(text);
              review(text);
            }}
            onSettings={onSettings}
          />
          <textarea
            className="input input-area mono"
            rows={8}
            aria-label="The model's reply"
            placeholder='{ "items": [ … ] }'
            value={reply}
            onChange={(event) => {
              setReply(event.target.value);
              setProblem(null);
            }}
          />
          {problem && <Notice tone="error">{problem}</Notice>}
          <div className="step-foot">
            <span className="muted small">Nothing is saved until you review it.</span>
            <button type="button" className="button" disabled={!reply.trim()} onClick={() => review()}>
              <Icon name="check" size={18} />
              Review
            </button>
          </div>
        </Panel>
      </div>

      {reviewed && (
        <Panel className="step-panel">
          <StepTitle n={4} title="Review" />
          <p className="muted small">
            Blanks start accepted. Anything that would replace what you wrote starts ignored.
          </p>

          {warnings.length > 0 && (
            <ul className="warnings">
              {warnings.map((warning) => (
                <li key={warning}>
                  <Notice tone="warn">{warning}</Notice>
                </li>
              ))}
            </ul>
          )}

          {reviewed.map(({ item, rows }) => {
            const outcome = outcomes[item.id];
            return (
              <section key={item.id} className="review-item">
                <h3 className="review-title">
                  <button type="button" className="link" onClick={() => onOpen(item.id)}>
                    {nameOf(item)}
                  </button>
                </h3>
                {outcome?.state === "saved" ? (
                  <Notice tone="ok">{`Saved ${plural(outcome.count, "change")}.`}</Notice>
                ) : (
                  <>
                    {outcome?.state === "failed" && <Notice tone="error">{outcome.reason}</Notice>}
                    <EnrichReview
                      item={item}
                      rows={rows}
                      choices={choices[item.id] ?? {}}
                      disabled={busy}
                      onChoose={(key, choice) =>
                        setChoices((current) => ({
                          ...current,
                          [item.id]: { ...current[item.id], [key]: choice },
                        }))
                      }
                    />
                  </>
                )}
              </section>
            );
          })}

          <div className="action-bar action-bar-float" data-unsaved={pending === 0 ? undefined : true}>
            <p className="action-bar-status">
              {pending === 0 ? "Nothing accepted" : `${plural(pending, "change")} to save`}
            </p>
            <button
              type="button"
              className="button"
              disabled={busy || pending === 0}
              onClick={() => void save()}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </Panel>
      )}
    </>
  );
}

const STEPS = ["Choose", "Copy prompt", "Paste reply", "Review"];

/** Where you are in the round trip. Decorative for layout, but announced as a list with the current one marked. */
function Steps({ at }: { at: number }) {
  return (
    <ol className="steps">
      {STEPS.map((label, index) => (
        <li
          key={label}
          className={index < at ? "step step-done" : index === at ? "step step-now" : "step"}
          aria-current={index === at ? "step" : undefined}
        >
          <span className="step-dot">{index < at ? <Icon name="check" size={14} /> : index + 1}</span>
          <span className="step-label">{label}</span>
        </li>
      ))}
    </ol>
  );
}

function StepTitle({ n, title }: { n: number; title: string }) {
  return (
    <h2 className="step-title">
      <span className="step-dot">{n}</span>
      {title}
    </h2>
  );
}
